// src/components/Monitors.js
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaStopCircle, FaTrashAlt, FaChartLine, FaClock, FaCheckCircle, FaExclamationTriangle, FaSync } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { loadUserMonitors, stopSymbolMonitoring, stopAllMonitoring, clearMonitors } from '../redux/slices/monitorSlice';
import axios from 'axios';

const API_BASE_URL = 'https://gritwears.store/api';

const formatTimeframe = (seconds) => {
  const timeframeMap = {
    60: '1 minute',
    120: '2 minutes',
    180: '3 minutes',
    300: '5 minutes',
    600: '10 minutes',
    900: '15 minutes',
    1800: '30 minutes',
    3600: '1 hour',
    7200: '2 hours',
    14400: '4 hours',
    28800: '8 hours',
    43200: '12 hours',
    86400: '1 day',
    604800: '1 week'
  };
  return timeframeMap[seconds] || `${seconds} seconds`;
};

const formatTimeframeShort = (seconds) => {
  const map = {
    60: '1m', 120: '2m', 180: '3m', 300: '5m', 600: '10m',
    900: '15m', 1800: '30m', 3600: '1h', 7200: '2h',
    14400: '4h', 28800: '8h', 43200: '12h', 86400: '1d', 604800: '1w'
  };
  return map[seconds] || `${seconds}s`;
};

const getSymbolDisplayName = (symbol) => {
  if (symbol.includes('Volatility')) return symbol;
  if (symbol.includes('Boom')) return symbol;
  if (symbol.includes('Crash')) return symbol;
  const symbolMap = {
    'EURUSD': 'EUR/USD', 'GBPUSD': 'GBP/USD', 'USDJPY': 'USD/JPY',
    'AUDUSD': 'AUD/USD', 'USDCAD': 'USD/CAD', 'USDCHF': 'USD/CHF',
    'NZDUSD': 'NZD/USD', 'BTCUSD': 'Bitcoin (BTC/USD)',
    'ETHUSD': 'Ethereum (ETH/USD)', 'XAUUSD': 'Gold (XAU/USD)',
    'XAGUSD': 'Silver (XAG/USD)'
  };
  return symbolMap[symbol] || symbol;
};

const AnalysisModal = ({ isOpen, onClose, analysis, loading, symbol, timeframe, userId, onUpdatePatterns }) => {
  const [localPatterns, setLocalPatterns] = useState({ choch: true, cisd: true, bos: true });
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (analysis?.enabledPatterns) {
      setLocalPatterns(analysis.enabledPatterns);
    }
  }, [analysis]);

  const handleUpdatePatterns = async (patterns) => {
    setIsUpdating(true);
    try {
      await axios.post(`${API_BASE_URL}/update-patterns`, {
        userId: userId,
        symbol: symbol,
        interval: timeframe,
        enabledPatterns: patterns
      });
      toast.success('Pattern settings updated');
      setLocalPatterns(patterns);
      if (onUpdatePatterns) {
        await onUpdatePatterns();
      }
    } catch (error) {
      console.error('Error updating patterns:', error);
      toast.error('Failed to update patterns');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle Watch All toggle in modal
  const handleWatchAllToggle = () => {
    const allEnabled = localPatterns.choch && localPatterns.cisd && localPatterns.bos;
    if (allEnabled) {
      // If all are enabled, disable all
      handleUpdatePatterns({ choch: false, cisd: false, bos: false });
    } else {
      // If not all enabled, enable all
      handleUpdatePatterns({ choch: true, cisd: true, bos: true });
    }
  };

  if (!isOpen) return null;

  const getCandleIcon = (type) => {
    if (type === 'bullish') return '🟢';
    if (type === 'bearish') return '🔴';
    return '🟡';
  };

  const getStrengthBadge = (strength) => {
    if (strength === 'high') return <span className="strength-high">HIGH</span>;
    if (strength === 'medium') return <span className="strength-medium">MEDIUM</span>;
    return <span className="strength-low">LOW</span>;
  };

  const allPatternsEnabled = localPatterns.choch && localPatterns.cisd && localPatterns.bos;

  return (
    <div className="analysis-modal-overlay" onClick={onClose}>
      <div className="analysis-modal" onClick={(e) => e.stopPropagation()}>
        <div className="analysis-modal-header">
          <h2><FaChartLine /> Position Analysis Report</h2>
          <button className="analysis-modal-close" onClick={onClose}>×</button>
        </div>
        
        {loading ? (
          <div className="analysis-loading">
            <div className="spinner"></div>
            <p>Analyzing market structure...</p>
          </div>
        ) : analysis ? (
          <div className="analysis-modal-body">
            <div className="analysis-symbol-info">
              <h3>{getSymbolDisplayName(analysis.symbol)} ({analysis.symbol})</h3>
              <div className="analysis-timeframe-badge"><FaClock /> {formatTimeframe(parseInt(analysis.timeframe))}</div>
            </div>

            <div className={`analysis-status ${analysis.dataStatus === 'live' ? 'status-live' : 'status-warning'}`}>
              {analysis.dataStatus === 'live' ? <><FaCheckCircle /> Live Data Active</> : <><FaExclamationTriangle /> Using Sample Data</>}
              <span className="status-time">Last update: {analysis.lastUpdateTime || 'Just now'}</span>
            </div>

            {analysis.active && (
              <div className="analysis-section">
                <h4>🎯 Pattern Watching Settings</h4>
                <div className="pattern-management">
                  {/* Watch All Toggle */}
                  <div className="watch-all-toggle-small">
                    <label className="toggle-switch-small">
                      <input 
                        type="checkbox" 
                        checked={allPatternsEnabled} 
                        onChange={handleWatchAllToggle}
                        disabled={isUpdating}
                      />
                      <span className="toggle-slider-small"></span>
                    </label>
                    <span className="toggle-label-small">
                      {allPatternsEnabled ? '✅ Watching All Patterns' : 'Select Patterns Manually'}
                    </span>
                  </div>
                  
                  <div className="pattern-status">
                    <div className={`pattern-status-item ${localPatterns.choch ? 'active' : 'inactive'}`}>
                      <span>🔄 CHoCH (Change of Character)</span>
                      <span className="status-badge">{localPatterns.choch ? '✅ Watching' : '⭕ Disabled'}</span>
                    </div>
                    <div className={`pattern-status-item ${localPatterns.cisd ? 'active' : 'inactive'}`}>
                      <span>⚡ CISD (Change in State of Delivery)</span>
                      <span className="status-badge">{localPatterns.cisd ? '✅ Watching' : '⭕ Disabled'}</span>
                    </div>
                    <div className={`pattern-status-item ${localPatterns.bos ? 'active' : 'inactive'}`}>
                      <span>📊 BOS (Break of Structure)</span>
                      <span className="status-badge">{localPatterns.bos ? '✅ Watching' : '⭕ Disabled'}</span>
                    </div>
                  </div>
                  
                  <div className="pattern-update-buttons">
                    <button 
                      onClick={() => handleUpdatePatterns({ choch: true, cisd: true, bos: true })} 
                      className="btn-small btn-primary" 
                      disabled={isUpdating || allPatternsEnabled}
                    >
                      👁️ Watch All
                    </button>
                    <button 
                      onClick={() => handleUpdatePatterns({ choch: true, cisd: false, bos: false })} 
                      className="btn-small" 
                      disabled={isUpdating}
                    >
                      🔄 Only CHoCH
                    </button>
                    <button 
                      onClick={() => handleUpdatePatterns({ choch: false, cisd: true, bos: false })} 
                      className="btn-small" 
                      disabled={isUpdating}
                    >
                      ⚡ Only CISD
                    </button>
                    <button 
                      onClick={() => handleUpdatePatterns({ choch: false, cisd: false, bos: true })} 
                      className="btn-small" 
                      disabled={isUpdating}
                    >
                      📊 Only BOS
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="analysis-section">
              <h4>📈 Recent Candle Pattern</h4>
              <div className="candles-table">
                <table>
                  <thead><tr><th>Position</th><th>Type</th><th>Open</th><th>High</th><th>Low</th><th>Close</th></tr></thead>
                  <tbody>
                    {analysis.recentCandles?.map((candle, idx) => (
                      <tr key={idx} className={candle.isOrderBlock ? 'order-block-row' : ''}>
                        <td>Candle {candle.position}</td>
                        <td>{getCandleIcon(candle.type)} {candle.type?.toUpperCase()}</td>
                        <td>{candle.open?.toFixed(5)}</td>
                        <td>{candle.high?.toFixed(5)}</td>
                        <td>{candle.low?.toFixed(5)}</td>
                        <td><strong>{candle.close?.toFixed(5)}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="analysis-section">
              <h4>📍 Order Blocks Detected</h4>
              <div className="order-blocks-grid">
                <div className="order-blocks-bullish">
                  <h5>🟢 Bullish OBs (Support - Lower)</h5>
                  {analysis.orderBlocks?.bullish?.length > 0 ? analysis.orderBlocks.bullish.map((ob, idx) => (
                    <div key={idx} className="order-block-item bullish">
                      <span className="ob-price">{ob.price?.toFixed(5)}</span>
                      <span className="ob-age">{ob.age} candles ago</span>
                      {getStrengthBadge(ob.strength)}
                    </div>
                  )) : <p className="no-data">No bullish OBs detected</p>}
                </div>
                <div className="order-blocks-bearish">
                  <h5>🔴 Bearish OBs (Resistance - Upper)</h5>
                  {analysis.orderBlocks?.bearish?.length > 0 ? analysis.orderBlocks.bearish.map((ob, idx) => (
                    <div key={idx} className="order-block-item bearish">
                      <span className="ob-price">{ob.price?.toFixed(5)}</span>
                      <span className="ob-age">{ob.age} candles ago</span>
                      {getStrengthBadge(ob.strength)}
                    </div>
                  )) : <p className="no-data">No bearish OBs detected</p>}
                </div>
              </div>
            </div>

            <div className="analysis-section">
              <h4>📐 Current Market Position</h4>
              <div className="position-diagram">
                <div className="resistance-level">🔴 RESISTANCE (Upper): {analysis.nearestBearishOB?.toFixed(5) || 'N/A'}</div>
                <div className="price-level">🟢 CURRENT PRICE: {analysis.currentPrice?.toFixed(5)}</div>
                <div className="support-level">🟢 SUPPORT (Lower): {analysis.nearestBullishOB?.toFixed(5) || 'N/A'}</div>
              </div>
              <div className="distance-info">
                <p>📏 Distance to Support: {analysis.distanceToSupport?.toFixed(5)} points below</p>
                <p>📏 Distance to Resistance: {analysis.distanceToResistance?.toFixed(5)} points above</p>
                <p className={analysis.pricePosition === 'between' ? 'between-ob' : 'at-ob'}>
                  {analysis.pricePosition === 'between' ? '📍 Price is trading BETWEEN Order Blocks' : '⚠️ Price is AT an Order Block level'}
                </p>
              </div>
            </div>

            <div className="analysis-section">
              <h4>🎯 Pattern Analysis</h4>
              <div className="pattern-insight"><p>{analysis.patternInsight}</p></div>
              {analysis.possibleScenarios && (
                <div className="possible-scenarios">
                  <h5>💡 Possible Scenarios:</h5>
                  <ul>{analysis.possibleScenarios.map((scenario, idx) => <li key={idx}>{scenario}</li>)}</ul>
                </div>
              )}
            </div>

            {analysis.signalLogs && analysis.signalLogs.length > 0 && (
              <div className="analysis-section">
                <h4>📋 Recent Signal Logs</h4>
                <div className="signal-logs-list">
                  {analysis.signalLogs.map((log, idx) => (
                    <div key={idx} className={`signal-log-item ${log.direction}`}>
                      <div className="signal-log-header">
                        <span className="signal-log-icon">{log.type === 'CHoCH' ? '🔄' : log.type === 'CISD' ? '⚡' : '📊'}</span>
                        <span className="signal-log-type">{log.type}</span>
                        <span className={`signal-log-direction ${log.direction}`}>
                          {log.direction === 'bullish' ? '🟢 BUY' : '🔴 SELL'}
                        </span>
                        <span className="signal-log-time">{log.readableTime || new Date(log.timestamp).toLocaleString()}</span>
                      </div>
                      <div className="signal-log-price">Price: {log.price?.toFixed(5)}</div>
                      {log.reason && <div className="signal-log-reason">{log.reason.split('\n')[0]}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="analysis-section">
              <h4>📡 Real-time Status</h4>
              <div className="realtime-status">
                <p>Data Source: {analysis.wsStatus === 'connected' ? '✅ REAL DATA' : '⏳ WAITING'}</p>
                <p>Total Candles: {analysis.totalCandles || 0}</p>
                <p>Swing Highs: {analysis.swingHighs || 0}</p>
                <p>Swing Lows: {analysis.swingLows || 0}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="analysis-error"><p>Failed to load analysis data</p></div>
        )}
      </div>
    </div>
  );
};

const Monitors = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { activeMonitors, loading } = useSelector((state) => state.monitors);
  const [selectedTimeframe, setSelectedTimeframe] = useState({});
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [selectedSymbolForAnalysis, setSelectedSymbolForAnalysis] = useState('');
  const [selectedTFForAnalysis, setSelectedTFForAnalysis] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (user?.uid) {
      refreshMonitors();
    } else {
      dispatch(clearMonitors());
    }
  }, [user?.uid, dispatch]);

  useEffect(() => {
    if (!user?.uid) return;
    if (window.socket) {
      const handleMonitorStopped = (data) => {
        console.log('Monitor stopped event received:', data);
        refreshMonitors();
      };
      window.socket.on('monitor_stopped', handleMonitorStopped);
      return () => window.socket.off('monitor_stopped', handleMonitorStopped);
    }
  }, [user?.uid]);

  const refreshMonitors = async () => {
    if (!user?.uid) return;
    setIsRefreshing(true);
    try {
      dispatch(clearMonitors());
      await dispatch(loadUserMonitors(user.uid));
    } catch (error) {
      console.error('Error refreshing monitors:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const fetchAnalysis = async (symbol, timeframe) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/analysis/${user.uid}/${symbol}/${timeframe}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching analysis:', error);
      return null;
    }
  };

  const groupedMonitors = () => {
    if (!activeMonitors || !Array.isArray(activeMonitors) || activeMonitors.length === 0) return [];
    const grouped = new Map();
    activeMonitors.forEach(monitor => {
      const symbol = monitor.symbol;
      if (!grouped.has(symbol)) {
        grouped.set(symbol, { 
          symbol, 
          timeframes: [], 
          startTime: monitor.startTime, 
          status: monitor.status || 'Active' 
        });
      }
      grouped.get(symbol).timeframes.push(monitor.interval);
    });
    for (const [key, group] of grouped) group.timeframes.sort((a, b) => a - b);
    return Array.from(grouped.values());
  };

  const handleStopSymbol = async (symbol) => {
    if (!window.confirm(`Stop monitoring all timeframes for ${getSymbolDisplayName(symbol)}?`)) return;
    try {
      await dispatch(stopSymbolMonitoring(user.uid, symbol));
      await refreshMonitors();
      toast.success(`Stopped monitoring ${getSymbolDisplayName(symbol)}`);
    } catch (error) {
      console.error('Error stopping monitor:', error);
      toast.error('Error stopping monitor');
    }
  };

  const handleStopAll = async () => {
    if (!window.confirm('Stop ALL active monitors? This action cannot be undone.')) return;
    try {
      await dispatch(stopAllMonitoring(user.uid));
      await refreshMonitors();
      toast.success('All monitors stopped');
    } catch (error) {
      console.error('Error stopping monitors:', error);
      toast.error('Error stopping monitors');
    }
  };

  const handleAnalyzePosition = async (symbol, timeframe) => {
    if (!timeframe) {
      toast.error('Please select a timeframe');
      return;
    }
    setSelectedSymbolForAnalysis(symbol);
    setSelectedTFForAnalysis(timeframe);
    setAnalysisLoading(true);
    setShowAnalysisModal(true);
    const data = await fetchAnalysis(symbol, timeframe);
    setAnalysisData(data);
    setAnalysisLoading(false);
  };

  const handleRefreshAnalysis = async () => {
    if (!selectedSymbolForAnalysis || !selectedTFForAnalysis) return;
    setAnalysisLoading(true);
    const data = await fetchAnalysis(selectedSymbolForAnalysis, selectedTFForAnalysis);
    setAnalysisData(data);
    setAnalysisLoading(false);
  };

  const handleTimeframeChange = (symbol, value) => {
    setSelectedTimeframe(prev => ({ ...prev, [symbol]: value }));
  };

  if (loading || isRefreshing) {
    return (
      <div className="card">
        <div className="card-header">
          <h2>Active Monitors</h2>
          <div className="monitor-actions">
            <button onClick={refreshMonitors} className="btn btn-secondary btn-small" disabled={isRefreshing}>
              <FaSync className={isRefreshing ? 'spinning' : ''} /> Refresh
            </button>
            <button onClick={handleStopAll} className="btn btn-danger">Stop All</button>
          </div>
        </div>
        <div className="loading-spinner-container">
          <div className="spinner-small"></div>
          <p>{isRefreshing ? 'Refreshing monitors...' : 'Loading monitors...'}</p>
        </div>
      </div>
    );
  }

  const grouped = groupedMonitors();

  if (!grouped || grouped.length === 0) {
    return (
      <div className="card">
        <div className="card-header">
          <h2>Active Monitors</h2>
          <div className="monitor-actions">
            <button onClick={refreshMonitors} className="btn btn-secondary btn-small"><FaSync /> Refresh</button>
            <button onClick={handleStopAll} className="btn btn-danger" disabled><FaTrashAlt /> Stop All</button>
          </div>
        </div>
        <div className="empty-state">
          <p className="text-muted">No active monitors</p>
          <p className="text-muted-small">Go to Dashboard to start monitoring trading pairs</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="card">
        <div className="card-header">
          <h2>Active Monitors ({grouped.length} symbols, {activeMonitors.length} timeframes)</h2>
          <div className="monitor-actions">
            <button onClick={refreshMonitors} className="btn btn-secondary btn-small"><FaSync /> Refresh</button>
            <button onClick={handleStopAll} className="btn btn-danger"><FaTrashAlt /> Stop All</button>
          </div>
        </div>
        <div className="monitor-list">
          {grouped.map((monitor) => {
            const displayName = getSymbolDisplayName(monitor.symbol);
            const timeframesList = monitor.timeframes.map(tf => formatTimeframeShort(tf)).join(' • ');
            const currentSelectedTF = selectedTimeframe[monitor.symbol] || monitor.timeframes[0];
            return (
              <div key={monitor.symbol} className="monitor-item-grouped">
                <div className="monitor-info">
                  <h4>🦅 {displayName} ({monitor.symbol})</h4>
                  <div className="monitor-details">
                    <p>⏱️ Active Timeframes: {timeframesList}</p>
                    <p>📊 Status: <span className="monitor-status">{monitor.status}</span></p>
                    <p>🕐 Started: {monitor.startTime ? new Date(monitor.startTime).toLocaleString() : 'Just now'}</p>
                  </div>
                  <div className="analysis-controls">
                    <select 
                      value={currentSelectedTF} 
                      onChange={(e) => handleTimeframeChange(monitor.symbol, parseInt(e.target.value))} 
                      className="timeframe-select"
                    >
                      {monitor.timeframes.map(tf => <option key={tf} value={tf}>{formatTimeframeShort(tf)}</option>)}
                    </select>
                    <button 
                      onClick={() => handleAnalyzePosition(monitor.symbol, currentSelectedTF)} 
                      className="btn btn-primary btn-small"
                    >
                      <FaChartLine /> Analyze Position
                    </button>
                  </div>
                </div>
                <button onClick={() => handleStopSymbol(monitor.symbol)} className="btn btn-danger">
                  <FaStopCircle /> Stop All
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <AnalysisModal 
        isOpen={showAnalysisModal}
        onClose={() => setShowAnalysisModal(false)}
        analysis={analysisData}
        loading={analysisLoading}
        symbol={selectedSymbolForAnalysis}
        timeframe={selectedTFForAnalysis}
        userId={user?.uid}
        onUpdatePatterns={handleRefreshAnalysis}
      />
    </>
  );
};

export default Monitors;